import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';

/**
 * Service to handle dynamic loading of CookieFirst consent management script
 * This service loads CookieFirst after Angular components are initialized,
 * ensuring the application loads first and then the cookie consent banner appears
 */
@Injectable({
  providedIn: 'root'
})
export class CookieFirstService {
  private scriptLoaded = false;
  private readonly cookieFirstScriptId = 'cookiefirst-js';
  private readonly cookieFirstUrl = 'https://consent.cookiefirst.com/sites/inviertispro.com-f3a0c90b-745d-4c98-80ae-76697acd3e9b/consent.js';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Loads CookieFirst script dynamically after Angular app initialization
   * Only loads in production environment (not localhost)
   */
  loadCookieFirst(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Only execute in browser environment
      if (!isPlatformBrowser(this.platformId)) {
        resolve();
        return;
      }

      // Check if script is already loaded
      if (this.scriptLoaded || this.document.getElementById(this.cookieFirstScriptId)) {
        resolve();
        return;
      }

      // Only load CookieFirst in production environment (not localhost)
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        resolve();
        return;
      }

      try {
        const script = this.document.createElement('script');
        script.id = this.cookieFirstScriptId;
        script.src = this.cookieFirstUrl;
        script.async = true;
        script.type = 'text/javascript';

        script.onload = () => {
          console.log('CookieFirst: Script loaded successfully');
          this.scriptLoaded = true;
          resolve();
        };

        script.onerror = (error) => {
          console.error('CookieFirst: Error loading script', error);
          reject(error);
        };

        this.document.head.appendChild(script);
        console.log('CookieFirst: Script loading started');
      } catch (error) {
        console.error('CookieFirst: Error creating script element', error);
        reject(error);
      }
    });
  }

  /**
   * Checks if CookieFirst script is loaded
   */
  isLoaded(): boolean {
    return this.scriptLoaded;
  }

  /**
   * Removes CookieFirst script if needed
   */
  removeCookieFirst(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const existingScript = this.document.getElementById(this.cookieFirstScriptId);
    if (existingScript) {
      existingScript.remove();
      this.scriptLoaded = false;
      console.log('CookieFirst: Script removed');
    }
  }
}